import { useEffect, useRef, useState } from 'react';
import {
  Card, CardContent, Stepper, Step, StepLabel, Button, Stack, TextField, MenuItem, Chip, Alert, Box, Typography,
  IconButton, Snackbar, CircularProgress,
} from '@mui/material';
import { Add, Delete, Save, Send, Close, MyLocation, Map as MapIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { FileUploadField } from '../../../components/FileUploadField';
import {
  siteOnboardingApi, SITE_TYPES, SITE_REGIONS, SITE_PROJECT_TYPES, SITE_CHARGER_TYPES, SITE_PRIORITIES, SITE_DOC_TYPES,
  type SiteApplication,
} from '../../../api/siteOnboarding.api';
import { projectsApi } from '../../../api/projects.api';
import { clientsApi } from '../../../api/clients.api';
import { employeesApi } from '../../../api/employees.api';
import { vendorsApi } from '../../../api/vendors.api';
import { techniciansApi } from '../../../api/technicians.api';
import { lookupsApi } from '../../../api/lookups.api';
import { extractApiError } from '../../../api/error';

const STEPS = ['Basic', 'Address', 'Geo', 'Stakeholder', 'Timeline', 'Classification', 'Priority', 'Ownership', 'Documents', 'Review'];
const cap = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '');
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', survey_pending: 'Survey Pending', survey_completed: 'Survey Completed', boq_pending: 'BOQ Pending',
  boq_approved: 'BOQ Approved', material_pending: 'Material Pending', installation_in_progress: 'Installation In Progress',
  testing_in_progress: 'Testing In Progress', commissioning_pending: 'Commissioning Pending', handover_pending: 'Handover Pending',
  completed: 'Completed', on_hold: 'On Hold', cancelled: 'Cancelled',
};

type Row = Record<string, unknown>;

/**
 * Extract latitude/longitude from a Google Maps URL. Handles the common shapes:
 *   .../@12.9716,77.5946,15z      ·   ?q=12.9716,77.5946   ·   q=loc:12.97,77.59
 *   ?ll=12.97,77.59               ·   !3d12.9716!4d77.5946 (place links)   ·   bare "lat,lng"
 * Returns null if no valid coordinate pair is found. (Short goo.gl/maps.app links can't be
 * resolved client-side — the user must open them to get the full URL first.)
 */
function parseLatLngFromUrl(url: string): { lat: number; lng: number } | null {
  const patterns = [
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
    /[?&]q=(?:loc:)?(-?\d+\.\d+),\s*(-?\d+\.\d+)/,
    /[?&]ll=(-?\d+\.\d+),\s*(-?\d+\.\d+)/,
    /(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) {
      const lat = Number(m[1]);
      const lng = Number(m[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) };
      }
    }
  }
  return null;
}

export function SiteOnboardingForm() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const id = Number(useParams<{ id: string }>().id);
  const queryKey = ['site-application-edit', id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => siteOnboardingApi.get(id) });
  const { data: projects } = useQuery({ queryKey: ['projects-lookup'], queryFn: () => projectsApi.list({ limit: 300 }) });
  const { data: clients } = useQuery({ queryKey: ['clients-lookup'], queryFn: () => clientsApi.list({ limit: 300 }) });
  const { data: employees } = useQuery({ queryKey: ['employees-lookup'], queryFn: () => employeesApi.list({ limit: 300 }) });
  const { data: vendors } = useQuery({ queryKey: ['vendors-lookup'], queryFn: () => vendorsApi.list({ limit: 300 }) });
  const { data: technicians } = useQuery({ queryKey: ['technicians-lookup'], queryFn: () => techniciansApi.list({ limit: 300 }) });
  const { data: lookups } = useQuery({ queryKey: ['lookups-all'], queryFn: () => lookupsApi.all() });

  const [form, setForm] = useState<Partial<SiteApplication>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(form);
  formRef.current = form;
  useEffect(() => { if (data) setForm(data); }, [data]);

  const editable = form.status === 'draft';
  const set = (k: keyof SiteApplication, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const clientName = clients?.data.find((c) => c.id === projects?.data.find((p) => p.id === form.project_id)?.clientId)?.clientName ?? form.client_name ?? '';

  function payload(autosave: boolean): Record<string, unknown> {
    const f = formRef.current;
    // application_code (Site ID) is now user-editable → keep it in the payload so it's saved.
    const { id: _id, status, site_id, client_id, google_map_url, delay_days, client_name, project_code, project_name, ...rest } = f as Record<string, unknown>;
    void _id; void status; void site_id; void client_id; void google_map_url; void delay_days; void client_name; void project_code; void project_name;
    return { ...rest, __autosave: autosave };
  }

  const saveMut = useMutation({ mutationFn: (a: boolean) => siteOnboardingApi.saveDraft(id, payload(a)), onSuccess: (res, a) => { setForm(res); if (!a) setToast('Draft saved'); }, onError: (e) => setToast(extractApiError(e)) });
  const submitMut = useMutation({ mutationFn: () => siteOnboardingApi.submit(id), onSuccess: (res) => { setForm(res); qc.invalidateQueries({ queryKey }); setSubmitted(true); }, onError: (e) => setToast(extractApiError(e)) });

  useEffect(() => {
    if (!editable) return undefined;
    const t = setInterval(() => saveMut.mutate(true), 120_000);
    return () => clearInterval(t);
  }, [editable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse latitude/longitude out of the pasted Google Map URL and fill the coordinate fields.
  const fetchLatLngFromUrl = () => {
    const url = (form.google_map_url ?? '').trim();
    if (!url) { setToast('Paste a Google Map URL first'); return; }
    const parsed = parseLatLngFromUrl(url);
    if (!parsed) { setToast('Could not read latitude/longitude from this URL — paste the full Google Maps link'); return; }
    setForm((f) => ({ ...f, latitude: parsed.lat, longitude: parsed.lng }));
    setToast(`Fetched ${parsed.lat}, ${parsed.lng} from the map URL`);
  };
  const openMap = () => {
    const url = form.google_map_url || (form.latitude != null && form.longitude != null ? `https://www.google.com/maps?q=${form.latitude},${form.longitude}` : null);
    if (url) window.open(url, '_blank', 'noopener');
    else setToast('Enter latitude & longitude first');
  };

  if (isLoading || !form.id) return <Box p={4} textAlign="center"><CircularProgress /></Box>;

  if (submitted) {
    return (
      <>
        <PageHeader title="Site Onboarding" subtitle={`Site ${form.application_code}`} />
        <Card><CardContent>
          <Alert severity="success" sx={{ mb: 2 }}>Site created successfully and is now in the lifecycle.</Alert>
          <Typography variant="subtitle2" gutterBottom>Suggested next actions</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => navigate('/field')}>Start Survey</Button>
            <Button variant="outlined" onClick={() => navigate('/boqs')}>Upload BOQ</Button>
            <Button variant="outlined" onClick={() => navigate('/vendor-applications')}>Assign Vendor</Button>
            <Button variant="outlined" onClick={() => navigate('/work-orders')}>Create Work Order</Button>
            <Button variant="outlined" onClick={() => navigate('/purchase-requisitions')}>Create Material Request</Button>
            <Button variant="outlined" onClick={() => navigate('/payments')}>Create Payment Request</Button>
            <Button variant="contained" onClick={() => navigate('/site-applications')}>Open Site Dashboard</Button>
          </Stack>
        </CardContent></Card>
      </>
    );
  }

  const T = (k: keyof SiteApplication, label: string, opts: { type?: string } = {}) => (
    <TextField label={label} type={opts.type ?? 'text'} value={(form[k] as string | number) ?? ''} disabled={!editable}
      onChange={(e) => set(k, opts.type === 'number' ? Number(e.target.value) : e.target.value)} sx={{ minWidth: 220 }} InputLabelProps={opts.type === 'date' ? { shrink: true } : undefined} />
  );
  const Sel = (k: keyof SiteApplication, label: string, options: readonly string[]) => (
    <TextField select label={label} value={(form[k] as string) ?? ''} disabled={!editable} onChange={(e) => set(k, e.target.value)} sx={{ minWidth: 220 }}>
      {options.map((o) => <MenuItem key={o} value={o}>{cap(o)}</MenuItem>)}
    </TextField>
  );
  const LookupSel = (k: keyof SiteApplication, label: string, opts: Array<{ id: number; name: string }>) => (
    <TextField select label={label} value={(form[k] as number) ?? ''} disabled={!editable} onChange={(e) => set(k, Number(e.target.value))} sx={{ minWidth: 220 }}>
      {opts.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
    </TextField>
  );
  const emps = (employees?.data ?? []).map((e) => ({ id: e.id, name: e.name }));
  const docs = (form.documents ?? []) as unknown as Row[];
  const setDocs = (rows: Row[]) => set('documents', rows);

  return (
    <>
      <PageHeader title="Site Onboarding" subtitle={`Site ${form.application_code}`}
        action={<Chip color={form.status === 'completed' ? 'success' : form.status === 'cancelled' ? 'error' : form.status === 'draft' ? 'default' : 'info'} label={STATUS_LABEL[form.status ?? 'draft'] ?? form.status} />} />

      {!editable && <Alert severity="info" sx={{ mb: 2 }}>This site is <b>{STATUS_LABEL[form.status ?? ''] ?? form.status}</b>. The creation form is locked; manage its lifecycle from Site Applications.</Alert>}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap' }}>{STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}</Stepper>

      <Card><CardContent>
        <Stack spacing={2}>
          {activeStep === 0 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <TextField label="Site ID *" value={form.application_code ?? ''} disabled={!editable} onChange={(e) => set('application_code', e.target.value)} sx={{ minWidth: 200 }} helperText="Enter manually (max 24 chars)" />
              <TextField select label="Project *" value={form.project_id ?? ''} disabled={!editable} onChange={(e) => set('project_id', Number(e.target.value))} sx={{ minWidth: 320 }}>
                {(projects?.data ?? []).map((p) => {
                  const cn = clients?.data.find((c) => c.id === p.clientId)?.clientName ?? '';
                  return <MenuItem key={p.id} value={p.id}>{p.projectCode} — {p.projectName}{cn ? ` (${cn})` : ''}</MenuItem>;
                })}
              </TextField>
              <TextField label="Client Name" value={clientName} disabled sx={{ minWidth: 220 }} />
              {T('site_name', 'Site Name *')}
              {Sel('site_type', 'Site Type *', SITE_TYPES)}
            </Stack>
          )}
          {activeStep === 1 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('site_address', 'Site Address *')}
              {T('landmark', 'Landmark')}
              {LookupSel('state_id', 'State *', (lookups?.states ?? []).map((s) => ({ id: s.id, name: s.name })))}
              {T('city', 'City *')}
              {T('district', 'District')}
              {T('pincode', 'Pin Code')}
              {/* Selecting a Region auto-fills Zone with the same value by default (still editable afterward). */}
              <TextField select label="Region" value={(form.region as string) ?? ''} disabled={!editable} sx={{ minWidth: 220 }}
                onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, region: v, zone: v })); }}>
                {SITE_REGIONS.map((o) => <MenuItem key={o} value={o}>{cap(o)}</MenuItem>)}
              </TextField>
              {T('zone', 'Zone')}
            </Stack>
          )}
          {activeStep === 2 && (
            <Stack spacing={2}>
              {/* Paste a Google Map URL, then "Fetch Lat/Long" pulls the coordinates out of it. */}
              <TextField label="Google Map URL" value={form.google_map_url ?? ''} disabled={!editable} fullWidth
                placeholder="Paste a Google Maps link, e.g. https://maps.google.com/?q=12.9716,77.5946"
                onChange={(e) => set('google_map_url', e.target.value)} />
              <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
                {T('latitude', 'Latitude *', { type: 'number' })}
                {T('longitude', 'Longitude *', { type: 'number' })}
                {editable && <Button startIcon={<MyLocation />} variant="contained" onClick={fetchLatLngFromUrl}>Fetch Lat/Long</Button>}
                <Button startIcon={<MapIcon />} variant="outlined" onClick={openMap}>View on Map</Button>
              </Stack>
            </Stack>
          )}
          {activeStep === 3 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('contact_person', 'Site Contact Person')}
              {T('contact_designation', 'Designation')}
              {T('contact_mobile', 'Mobile Number')}
              {T('contact_alternate_mobile', 'Alternate Mobile')}
              {T('contact_email', 'Email ID')}
              {T('escalation_person', 'Escalation Contact Person')}
              {T('escalation_mobile', 'Escalation Mobile')}
            </Stack>
          )}
          {activeStep === 4 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('allocation_date', 'Site Allocation Date *', { type: 'date' })}
              {T('survey_planned_date', 'Survey Planned Date', { type: 'date' })}
              {T('survey_completion_date', 'Survey Completion Date', { type: 'date' })}
              {T('client_committed_handover_date', 'Client Committed Handover *', { type: 'date' })}
              {T('gigforce_committed_date', 'Gigforce Committed Date *', { type: 'date' })}
              {T('actual_handover_date', 'Actual Handover Date', { type: 'date' })}
              <TextField label="Delay Days (auto)" value={form.delay_days ?? 0} disabled sx={{ minWidth: 160 }} />
            </Stack>
          )}
          {activeStep === 5 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('oem', 'OEM *')}
              {T('cpo', 'CPO *')}
              {Sel('project_type', 'Project Type *', SITE_PROJECT_TYPES)}
              {Sel('charger_type', 'Charger Type', SITE_CHARGER_TYPES)}
              {T('charger_capacity', 'Charger Capacity (e.g. 60 KW)')}
              {T('number_of_chargers', 'Number of Chargers', { type: 'number' })}
            </Stack>
          )}
          {activeStep === 6 && <Stack direction="row" gap={2}>{Sel('priority', 'Priority *', SITE_PRIORITIES)}</Stack>}
          {activeStep === 7 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {LookupSel('project_manager_id', 'Project Manager *', emps)}
              {LookupSel('regional_ops_manager_id', 'Regional Operations Manager *', emps)}
              {LookupSel('state_asm_id', 'State ASM', emps)}
              {LookupSel('site_engineer_id', 'Site Engineer', emps)}
              {LookupSel('assigned_vendor_id', 'Assigned Vendor', (vendors?.data ?? []).map((v) => ({ id: v.id, name: v.vendorName })))}
              {LookupSel('assigned_technician_id', 'Assigned Technician', (technicians?.data ?? []).map((t) => ({ id: t.id, name: t.technicianName })))}
            </Stack>
          )}
          {activeStep === 8 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>Record site documents (Photos, Survey Report, Approved BOQ, Drawings, Layout, Site Acceptance, Handover). Files are tracked by URL.</Alert>
              <Stack spacing={1.5}>
                {docs.map((row, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField select size="small" label="Type" value={(row.document_type as string) ?? ''} disabled={!editable} sx={{ minWidth: 190 }}
                      onChange={(e) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, document_type: e.target.value } : r)))}>
                      {SITE_DOC_TYPES.map((d) => <MenuItem key={d} value={d}>{cap(d)}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="File Name" value={(row.file_name as string) ?? ''} disabled={!editable} onChange={(e) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, file_name: e.target.value } : r)))} sx={{ minWidth: 180 }} />
                    <FileUploadField label="File" disabled={!editable} value={(row.file_url as string) ?? null} onChange={(url) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, file_url: url ?? '' } : r)))} />
                    {editable && <IconButton size="small" color="error" onClick={() => setDocs(docs.filter((_, idx) => idx !== i))}><Delete fontSize="small" /></IconButton>}
                  </Stack>
                ))}
              </Stack>
              {editable && <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setDocs([...docs, { document_type: 'site_photos' }])}>Add document</Button>}
            </Box>
          )}
          {activeStep === 9 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Review &amp; Submit</Typography>
              <Typography variant="body2" color="text.secondary">Site: <b>{form.site_name}</b> · Project: {projects?.data.find((p) => p.id === form.project_id)?.projectName ?? '—'} · Client: {clientName || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Type: {cap(form.site_type)} · Priority: {cap(form.priority)} · Chargers: {form.number_of_chargers ?? '—'} · Docs: {docs.length}</Typography>
              {!form.project_id && <Alert severity="warning">A project must be selected before submission.</Alert>}
              {(form.latitude == null) !== (form.longitude == null) && <Alert severity="warning">Latitude and longitude must be provided together.</Alert>}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" mt={3}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>Previous</Button>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<Close />} color="inherit" onClick={() => navigate('/site-applications')}>Cancel</Button>
            {editable && <Button startIcon={<Save />} variant="outlined" disabled={saveMut.isPending} onClick={() => saveMut.mutate(false)}>Save Draft</Button>}
            {activeStep < STEPS.length - 1 && <Button variant="contained" onClick={() => { if (editable) saveMut.mutate(true); setActiveStep((s) => s + 1); }}>Next</Button>}
            {activeStep === STEPS.length - 1 && editable && <Button startIcon={<Send />} variant="contained" color="success" disabled={submitMut.isPending} onClick={() => submitMut.mutate()}>Submit</Button>}
          </Stack>
        </Stack>
      </CardContent></Card>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
